import app from '../app';
import Modal, { IInternalModalAttrs } from './Modal';
import Button from './Button';
import Stream from '../utils/Stream';
import type Mithril from 'mithril';
import type User from '../models/User';

export interface IImMuteModalAttrs extends IInternalModalAttrs {
  user: User;
}

export default class ImMuteModal<CustomAttrs extends IImMuteModalAttrs = IImMuteModalAttrs> extends Modal<CustomAttrs> {
  protected selectedDuration: Stream<string> = Stream('1天');
  protected customDuration: Stream<string> = Stream('');
  protected pkgId: string = '';
  protected muteTimeLeft: Stream<string> = Stream('');
  protected loading: boolean = false;

  oninit(vnode: Mithril.Vnode<CustomAttrs, this>) {
    super.oninit(vnode);
    // 默认选中第一个游戏
    const gameList = app.store.all('pkgLogo');
    if (gameList.length > 0) {
      this.pkgId = gameList[0].id();
      this.fetchMuteTimeLeft();
    }
  }

  className() {
    return 'ImMuteModal Modal--small';
  }

  title() {
    return '禁言';
  }
  // 获取禁言剩余时间
  fetchMuteTimeLeft() {
    if (!this.pkgId) {
      this.muteTimeLeft('');
      return;
    }
    app
      .request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/chatGetMember',
        params: {
          userID: this.attrs.user.uniqueID(),
          pkgId: this.pkgId,
        },
      })
      .then((result: any) => {
        if (result.diffInSeconds > 0) {
          this.muteTimeLeft(this.formatTime(result.diffInSeconds));
        } else {
          this.muteTimeLeft('未禁言');
        }
        m.redraw();
      })
      .catch(() => {
        this.muteTimeLeft('查询失败');
        m.redraw();
      });
  }

  formatTime(seconds: number): string {
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    let str = '';
    if (d) str += `${d}天`;
    if (h) str += `${h}小时`;
    if (m) str += `${m}分钟`;
    if (!str) str = '小于1分钟';
    return str;
  }

  content() {
    const presetDurations = [
      { value: '10分钟', label: '10分钟' },
      { value: '1小时', label: '1小时' },
      { value: '12小时', label: '12小时' },
      { value: '1天', label: '1天' },
    ];

    const gameList = app.store.all('pkgLogo');

    return (
      <div className="Modal-body">
        <div className="Form">
          {/* 选择游戏 */}
          <div className="Form-group">
            <label>选择游戏</label>
            <div className="ImMuteModal-gameOptions">
              <select
                className={'Button UserListPage-createUserBtn'}
                onchange={(e: any) => {
                  if (e.target.value === 'empty') return;
                  this.pkgId = e.target.value;
                  this.fetchMuteTimeLeft();
                }}
                value={this.pkgId}
              >
                {gameList.map((category: any) => {
                  let localePkgName = '';
                  if (category.pkgName && typeof category.pkgName === 'function' && category.pkgName()) {
                    try {
                      const obj = JSON.parse(category.pkgName());
                      const map = new Map(Object.entries(obj));
                      localePkgName = map.get(app.data.locale) || '';
                    } catch {
                      localePkgName = category.pkgName();
                    }
                  }
                  return (
                    <option value={category.id()} key={category.id()}>
                      {localePkgName}
                    </option>
                  );
                })}
              </select>
            </div>
            {/* 剩余禁言时间 */}
            <div className="ImMuteModal-muteTimeLeft" style="margin-top:8px;color:#f56c6c;">
              当前禁言剩余时间：{this.muteTimeLeft()}
            </div>
          </div>
          {/* 预设时长选项 */}
          <div className="Form-group">
            <label>选择禁言时长</label>
            <div className="ImMuteModal-presetOptions">
              {presetDurations.map((duration) => (
                <label className="checkbox" key={duration.value}>
                  <input
                    type="radio"
                    name="duration"
                    value={duration.value}
                    checked={this.selectedDuration() === duration.value && this.customDuration() === ''}
                    onchange={(e: Event) => {
                      const target = e.target as HTMLInputElement;
                      this.selectedDuration(target.value);
                      this.customDuration('');
                      m.redraw();
                    }}
                  />
                  {duration.label}
                </label>
              ))}
            </div>
          </div>
          {/* 自定义时长 */}
          <div className="Form-group">
            <label>自定义时长（天）</label>
            <div className="ImMuteModal-customDuration">
              <input
                className="FormControl"
                type="number"
                placeholder="请输入自定义时长（天）"
                value={this.customDuration()}
                onchange={(e: Event) => {
                  const target = e.target as HTMLInputElement;
                  this.customDuration(target.value);
                  if (target.value !== '') {
                    this.selectedDuration('');
                  }
                  m.redraw();
                }}
                min="0"
                step="0.1"
              />
            </div>
          </div>
          {/* 提交按钮 */}
          <div className="Form-group">
            <Button
              className="Button Button--primary"
              type="button"
              loading={this.loading}
              onclick={this.onsubmit.bind(this)}
              disabled={!this.pkgId}
            >
              确定
            </Button>
          </div>
        </div>
      </div>
    );
  }

  onsubmit() {
    if (!this.pkgId) {
      app.alerts.show({ type: 'error' }, '请先选择游戏');
      return;
    }
    this.loading = true;

    let durationInSeconds = 0;
    const customValue = parseFloat(this.customDuration());

    if (customValue >= 0 && this.customDuration() !== '') {
      // 允许为0，0表示取消禁言
      durationInSeconds = Math.floor(customValue * 24 * 60 * 60);
    } else {
      const selectedDuration = this.selectedDuration();
      switch (selectedDuration) {
        case '10分钟':
          durationInSeconds = 10 * 60;
          break;
        case '1小时':
          durationInSeconds = 60 * 60;
          break;
        case '12小时':
          durationInSeconds = 12 * 60 * 60;
          break;
        case '1天':
          durationInSeconds = 24 * 60 * 60;
          break;
        default:
          durationInSeconds = 24 * 60 * 60;
      }
    }

    // 调用API来执行禁言操作
    app
      .request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/chatMuteMember',
        params: {
          userID: this.attrs.user.uniqueID(),
          pkgId: this.pkgId,
          mutedSeconds: durationInSeconds,
        },
      })
      .then(() => {
        this.loading = false;
        this.hide();
        if (durationInSeconds === 0) {
          app.alerts.show({ type: 'success' }, `用户 ${this.attrs.user.username()} 已取消禁言`);
        } else {
          const durationText = customValue > 0 ? `${customValue}天` : this.selectedDuration();
          app.alerts.show({ type: 'success' }, `用户 ${this.attrs.user.username()} 已被禁言 ${durationText}`);
        }
        this.fetchMuteTimeLeft();
      })
      .catch((error) => {
        this.loading = false;
        this.onerror(error);
      });
  }
}
